/**
 * Progressive Web App helpers: register SW early, capture install prompt.
 */

const INSTALL_DISMISS_KEY = 'pwa_install_dismissed_until';
const SNOOZE_DAYS = 14;

export function isStandaloneDisplay() {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

export function isIosSafari() {
    const ua = window.navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return iOS && webkit;
}

export function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        return Promise.resolve(null);
    }

    return navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => {
        console.warn('Service worker registration failed', err);
        return null;
    });
}

export function shouldShowInstallHint() {
    if (isStandaloneDisplay()) {
        return false;
    }
    try {
        const until = localStorage.getItem(INSTALL_DISMISS_KEY);
        if (until && Date.now() < Number(until)) {
            return false;
        }
    } catch (e) {
        // ignore storage errors
    }
    return true;
}

export function dismissInstallHint() {
    try {
        const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
        localStorage.setItem(INSTALL_DISMISS_KEY, String(until));
    } catch (e) {
        // ignore
    }
}

let deferredPrompt = null;

export function getDeferredInstallPrompt() {
    return deferredPrompt;
}

export function captureInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        deferredPrompt = event;
        window.dispatchEvent(new CustomEvent('pwa:installable'));
    });

    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        dismissInstallHint();
        window.dispatchEvent(new CustomEvent('pwa:installed'));
    });
}

export async function promptInstall() {
    if (!deferredPrompt) {
        return { ok: false, reason: 'no_prompt' };
    }
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    if (choice.outcome === 'accepted') {
        dismissInstallHint();
        return { ok: true, reason: 'accepted' };
    }
    return { ok: false, reason: 'dismissed' };
}

export function bootPwa() {
    captureInstallPrompt();
    if (document.readyState === 'complete') {
        registerServiceWorker();
    } else {
        window.addEventListener('load', () => registerServiceWorker());
    }
}
